import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  ArrowRight,
  Bot,
  CalendarClock,
  CheckCircle2,
  Coins,
  CreditCard,
  Download,
  KeyRound,
  LogOut,
  Mail,
  MessageCircle,
  RefreshCw,
  Repeat2,
  Search,
  ShieldCheck,
  Star,
  Ticket,
  UserRound,
  WalletCards,
  X,
} from "lucide-react";
import { API_BASE_URL as API } from "../apiConfig";
import RetailMemberAuthPanel from "../components/retail/RetailMemberAuthPanel";
import PublicBrandMark from "../components/public/PublicBrandMark";
import PublicLanguageSwitcher from "../components/public/PublicLanguageSwitcher";
import { getTokyoDateKey } from "../utils/tokyoDate";

const retailTokenKey = "retail_member_token";
const supportEmail = "premiumtaxibooking@gmail.com";
const whatsappPhone = (process.env.REACT_APP_PUBLIC_BOOKING_WHATSAPP_PHONE || "817036670926").replace(/\D/g, "");

const vehicleNames = {
  alpha: "Toyota Alphard",
  alphard: "Toyota Alphard",
  "阿尔法": "Toyota Alphard",
  hiace: "Toyota HiAce",
  premium_hiace: "Premium HiAce",
  luxury_hiace: "Premium HiAce",
};

const languageOptions = [
  ["en", "EN"],
  ["ja", "日本語"],
  ["zh", "中国語"],
  ["zhHant", "繁体字"],
];

const memberGuestPresentationCopy = {
  en: {
    eyebrow: "Private travel, personally managed",
    titleLines: ["Every journey.", "One elegant account."],
    body: "Your reservations, payments, receipts and rewards—thoughtfully organised in one private space.",
    benefits: [
      ["3–5% Rewards", "Earn points on completed member journeys."],
      ["One Private Space", "Bookings, payments and receipts together."],
      ["Personal Assistance", "Support from our team when plans change."],
    ],
    imageAlt: "Shonan International premium chauffeur service",
  },
  ja: {
    eyebrow: "旅を、より上質に、よりスマートに",
    titleLines: ["すべての旅を。", "ひとつの美しいアカウントで。"],
    compactTitle: true,
    body: "ご予約、お支払い、領収書、会員特典を、ひとつのプライベート空間でスマートに管理できます。",
    benefits: [
      ["3〜5%ポイント", "会員予約のご利用完了後にポイントを獲得。"],
      ["一括管理", "予約・支払い・領収書をまとめて確認。"],
      ["専任サポート", "予定変更の際もスタッフが丁寧に対応。"],
    ],
    imageAlt: "湘南国際のプレミアム送迎サービス",
  },
  zh: {
    eyebrow: "每一次出行，都被悉心管理",
    titleLines: ["所有旅程。", "尽在一个尊贵账户。"],
    body: "预约、付款、收据与会员奖励，都可在专属空间内轻松管理。",
    benefits: [
      ["3%–5% 积分", "会员行程完成后自动获得积分。"],
      ["一站式管理", "预约、付款与收据集中查看。"],
      ["专属协助", "行程变化时由团队及时提供支持。"],
    ],
    imageAlt: "Shonan International 高端包车服务",
  },
  zhHant: {
    eyebrow: "每一次出行，都被悉心管理",
    titleLines: ["所有旅程。", "盡在一個尊貴帳戶。"],
    body: "預約、付款、收據與會員獎勵，都可在專屬空間內輕鬆管理。",
    benefits: [
      ["3%–5% 積分", "會員行程完成後自動獲得積分。"],
      ["一站式管理", "預約、付款與收據集中查看。"],
      ["專屬協助", "行程變化時由團隊及時提供支援。"],
    ],
    imageAlt: "Shonan International 高端包車服務",
  },
};

const countryOptions = [
  { value: "Japan", labels: { en: "Japan", ja: "日本", zh: "日本", zhHant: "日本" } },
  { value: "China", labels: { en: "China", ja: "中国", zh: "中国大陆", zhHant: "中國大陸" } },
  { value: "Taiwan", labels: { en: "Taiwan", ja: "台湾", zh: "中国台湾", zhHant: "台灣" } },
  { value: "Hong Kong", labels: { en: "Hong Kong", ja: "香港", zh: "中国香港", zhHant: "香港" } },
  { value: "United States", labels: { en: "United States", ja: "アメリカ", zh: "美国", zhHant: "美國" } },
  { value: "Singapore", labels: { en: "Singapore", ja: "シンガポール", zh: "新加坡", zhHant: "新加坡" } },
  { value: "Australia", labels: { en: "Australia", ja: "オーストラリア", zh: "澳大利亚", zhHant: "澳洲" } },
  { value: "Other", labels: { en: "Other", ja: "その他", zh: "其他", zhHant: "其他" } },
];

const memberPageCopy = {
  en: {
    guestCenter: "Guest Member Center",
    signInTitle: "Sign in to view your reservations and rewards",
    signInBody: "My Page is for guest members. You can check reservations, payments, receipts, credit, and points in one place.",
    newBooking: "New Booking",
    signOut: "Sign out",
    myPage: "My Page",
    welcome: "Welcome",
    overview: {
      upcoming: "Upcoming Reservations",
      awaitingPayment: "Awaiting Payment",
      availableCredit: "Available Credit",
      points: "Points",
      memberStatus: "Member Status",
      active: "Active",
      pointsRate: "Points Rate",
      cumulativeSpend: "Cumulative Spend",
      nextTier: "To Next Tier",
      topTier: "Top tier",
    },
    reservations: {
      title: "My Reservations",
      body: "Sorted by service date and time. Choose a category to quickly find the booking you need.",
      order: "Service time order",
      year: "Year",
      allYears: "All years",
      month: "Month",
      allMonths: "All months",
      search: "Search Reservations",
      searchPlaceholder: "Name, date, code, customer code, pickup or drop-off",
      empty: "No linked reservations yet. Book as a member or register with the same email address or phone number used for past bookings.",
      noMatch: "No reservations match the selected category, month, or search keyword.",
    },
    reservationTabs: {
      pending_payment: "Awaiting Payment",
      upcoming: "Upcoming",
      refund_aftercare: "Refund / Support",
      pending_review: "Awaiting Review",
      all: "All Reservations",
    },
    common: {
      reservation: "Reservation",
      privateRide: "Private ride",
      datePending: "Date pending",
      paymentDatePending: "Payment date pending",
      toBeConfirmed: "To be confirmed",
      notSet: "Not set",
      amount: "Amount",
      customerName: "Customer Name",
      customerCode: "Customer Code",
      serviceDate: "Service Date",
      serviceType: "Service Type",
      pickupLocation: "Pickup Location",
      dropoffLocation: "Drop-off Location",
      vehicleType: "Vehicle Type",
      paymentStatus: "Payment Status",
      reservationStatus: "Reservation Status",
      payNow: "Pay Now",
      viewDetails: "View Details",
      bookAgain: "Book Again",
      reviewService: "Review Service",
      downloadReceiptPdf: "Download Receipt PDF",
      receiptAfterPayment: "Receipt available after payment",
      squarePayment: "Square secure payment",
      afterPayment: "After payment",
      close: "Close",
    },
    status: {
      refunded: "Refunded",
      paid: "Paid",
      unpaid: "Unpaid",
      cancelled: "Cancelled",
      completed: "Completed",
      confirmed: "Confirmed",
      awaitingConfirmation: "Awaiting confirmation",
    },
    serviceTypes: {
      privateCharter: "Private Charter",
      airportPickup: "Airport Pickup",
      airportDropoff: "Airport Drop-off",
      privateTransfer: "Private Transfer",
    },
    payments: {
      title: "Payment Records / Receipts",
      body: "Download a formal PDF receipt with company information, tax details, and reservation details.",
      combinedTitle: "Combined Receipt PDF",
      combinedBody: "Set a receipt name for this PDF and select one or more paid reservations to combine.",
      receiptName: "Receipt Name",
      receiptNamePlaceholder: "Name shown on this receipt",
      selectedTotal: "Selected Total",
      selectedCount: "{count} reservation(s) selected",
      downloadCombined: "Download Combined PDF",
      generating: "Generating...",
      selectAllPaid: "Select all paid",
      clear: "Clear",
      noRecords: "No payment records yet.",
      selectAtLeastOne: "Please select at least one paid reservation.",
      generated: "Receipt PDF has been generated.",
      failed: "Unable to generate the receipt PDF.",
    },
    receiptGuide: {
      title: "Receipt PDF Guide",
      headline: "Create a formal receipt for your booking.",
      body: "Select one paid reservation for a single receipt, or select multiple paid reservations to create one combined PDF.",
      before: "Before downloading",
      step1Title: "Set receipt name",
      step1Body: "Enter the name or company name that should appear as the receipt addressee.",
      step2Title: "Select paid bookings",
      step2Body: "Only paid reservations can be included in a receipt PDF.",
      step3Title: "Download PDF",
      step3Body: "The PDF includes company information, tax details, payment status, and booking details.",
      includesTitle: "Official PDF includes",
      includes: [
        "領収書 title and receipt number",
        "Receipt addressee selected by the customer",
        "湘南国際株式会社 issuer details and company seal",
        "Registration number and consumption tax amount",
        "Square full payment status",
      ],
    },
    rewards: {
      title: "My Coupons / Rewards Wallet",
      body: "All account coupons, credit, and points are saved here for future rides.",
      availableCredit: "Available Credit",
      pointsBalance: "Points Balance",
      savedCoupons: "Saved Coupons",
      pointRule: "Paid member reservations earn 3% to 5% points based on your membership level. Past paid reservations are credited automatically when matched. Points are valid for 180 days.",
      reviewRule: "Verified service reviews can add reward credit to your account balance.",
      cashRule: "Points cannot be exchanged for cash.",
      couponTitle: "My Coupons",
      couponBody: "Positive credit rewards are stored as account coupons and can be used as balance on future guest bookings.",
      noCoupons: "No saved coupons yet. Complete a verified service review to receive reward credit.",
      redeemTitle: "Redeem Coupon Code",
      redeemBody: "Enter a coupon code to add the reward amount to your account credit.",
      redeemPlaceholder: "Enter coupon code",
      redeemButton: "Redeem",
      redeeming: "Redeeming...",
      redeemSuccess: "Coupon redeemed successfully. Credit has been added to your account.",
      redeemFailed: "Unable to redeem this coupon code.",
      rewardHistory: "Reward history",
      noHistory: "No credit or points history yet.",
      accountCoupon: "Account Coupon",
      defaultLedgerType: "Reward credit",
      defaultCreditNote: "Saved reward credit for your next ride.",
      issued: "Issued",
      savedInBalance: "Saved in balance",
      pointsUnit: "pts",
    },
    profile: {
      title: "Profile & Security",
      body: "Update your customer information, receipt details, and account password.",
      personalTitle: "Personal / Business Information",
      personalBody: "This information is used for future bookings, receipts, and member rewards.",
      name: "Name",
      emailLoginId: "Email Login ID",
      phone: "Phone",
      countryRegion: "Country / Region",
      preferredLanguage: "Preferred Language",
      receiptTitle: "Receipt / Billing Information",
      receiptBody: "Optional details for receipt display and future customer support.",
      receiptName: "Receipt Name",
      receiptNamePlaceholder: "Name shown on receipt",
      companyName: "Company Name",
      companyNamePlaceholder: "Optional company name",
      billingAddress: "Billing Address",
      billingAddressPlaceholder: "Optional address",
      save: "Save Information",
      saving: "Saving...",
      saved: "Profile and receipt information updated.",
      saveFailed: "Unable to update profile information.",
      changePassword: "Change Password",
      passwordBody: "Use your current password to protect your member account.",
      currentPassword: "Current Password",
      newPassword: "New Password",
      confirmNewPassword: "Confirm New Password",
      updatePassword: "Update Password",
      updating: "Updating...",
      passwordUpdated: "Password updated successfully.",
      passwordFailed: "Unable to update password.",
    },
    support: {
      title: "Contact Support",
      body: "For booking questions, payment help, or urgent service issues.",
      email: "Email",
      whatsapp: "WhatsApp",
      whatsappBody: "Contact our team",
      ai: "AI Concierge",
      aiBody: "Use the chat button on this page",
    },
    detail: {
      title: "Reservation Details",
    },
    system: {
      loading: "Loading My Page...",
      loadFailed: "Unable to load My Page.",
      refresh: "Refresh My Page",
    },
  },
  ja: {
    guestCenter: "お客様メンバーセンター",
    signInTitle: "予約と特典を確認するにはログインしてください",
    signInBody: "My Pageでは、予約、支払い、領収書、残高、ポイントをまとめて確認できます。",
    newBooking: "新規予約",
    signOut: "ログアウト",
    myPage: "マイページ",
    welcome: "ようこそ",
    overview: {
      upcoming: "ご利用前の予約",
      awaitingPayment: "未払い",
      availableCredit: "利用可能残高",
      points: "ポイント",
      memberStatus: "会員ステータス",
      active: "有効",
      pointsRate: "ポイント還元率",
      cumulativeSpend: "累計利用金額",
      nextTier: "次ランクまで",
      topTier: "最高ランク",
    },
    reservations: {
      title: "予約履歴",
      body: "ご利用日時順に表示しています。カテゴリを選ぶと必要な予約をすぐ確認できます。",
      order: "ご利用日時順",
      year: "年度",
      allYears: "すべての年度",
      month: "月",
      allMonths: "すべての月",
      search: "予約検索",
      searchPlaceholder: "名前、日付、予約番号、顧客コード、乗車地、降車地",
      empty: "紐づいている予約はまだありません。会員として予約するか、過去予約と同じメールアドレスまたは電話番号で登録してください。",
      noMatch: "選択したカテゴリ、月、検索条件に一致する予約はありません。",
    },
    reservationTabs: {
      pending_payment: "未払い",
      upcoming: "ご利用前",
      refund_aftercare: "返金／サポート",
      pending_review: "レビュー待ち",
      all: "すべての予約",
    },
    common: {
      reservation: "予約",
      privateRide: "プライベート送迎",
      datePending: "日付未定",
      paymentDatePending: "支払日未定",
      toBeConfirmed: "確認中",
      notSet: "未設定",
      amount: "金額",
      customerName: "お客様名",
      customerCode: "お客様コード",
      serviceDate: "ご利用日時",
      serviceType: "サービス種別",
      pickupLocation: "乗車場所",
      dropoffLocation: "降車場所",
      vehicleType: "車種",
      paymentStatus: "支払状況",
      reservationStatus: "予約状況",
      payNow: "今すぐ支払う",
      viewDetails: "詳細を見る",
      bookAgain: "再予約する",
      reviewService: "サービスを評価",
      downloadReceiptPdf: "領収書PDFをダウンロード",
      receiptAfterPayment: "領収書はお支払い後に発行できます",
      squarePayment: "Square安全決済",
      afterPayment: "支払い後",
      close: "閉じる",
    },
    status: {
      refunded: "返金済み",
      paid: "支払済み",
      unpaid: "未払い",
      cancelled: "キャンセル済み",
      completed: "完了",
      confirmed: "確定済み",
      awaitingConfirmation: "確認待ち",
    },
    serviceTypes: {
      privateCharter: "貸切チャーター",
      airportPickup: "空港お迎え",
      airportDropoff: "空港お送り",
      privateTransfer: "片道送迎",
    },
    payments: {
      title: "支払い履歴 / 領収書",
      body: "会社情報、税額、ご予約内容を含む正式なPDF領収書をダウンロードできます。",
      combinedTitle: "合算領収書PDF",
      combinedBody: "領収書の宛名を設定し、支払済みの予約を1件以上選択して合算できます。",
      receiptName: "領収書宛名",
      receiptNamePlaceholder: "領収書に表示する宛名",
      selectedTotal: "選択合計",
      selectedCount: "{count}件の予約を選択中",
      downloadCombined: "合算PDFをダウンロード",
      generating: "作成中...",
      selectAllPaid: "支払済みをすべて選択",
      clear: "クリア",
      noRecords: "支払い履歴はまだありません。",
      selectAtLeastOne: "支払済みの予約を1件以上選択してください。",
      generated: "領収書PDFを作成しました。",
      failed: "領収書PDFを作成できませんでした。",
    },
    receiptGuide: {
      title: "領収書PDFガイド",
      headline: "ご予約の正式な領収書を作成できます。",
      body: "支払済み予約1件の領収書、または複数予約をまとめた合算PDFを作成できます。",
      before: "ダウンロード前の確認",
      step1Title: "宛名を設定",
      step1Body: "領収書に表示するお名前または会社名を入力してください。",
      step2Title: "支払済み予約を選択",
      step2Body: "領収書PDFには支払済み予約のみ含めることができます。",
      step3Title: "PDFをダウンロード",
      step3Body: "PDFには会社情報、税額、支払状況、ご予約内容が含まれます。",
      includesTitle: "正式PDFに含まれる内容",
      includes: [
        "領収書タイトルと領収書番号",
        "お客様が指定した領収書宛名",
        "湘南国際株式会社の発行元情報と印章",
        "登録番号と消費税額",
        "Square全額決済ステータス",
      ],
    },
    rewards: {
      title: "クーポン / 特典ウォレット",
      body: "アカウントのクーポン、残高、ポイントは次回のご利用に保存されます。",
      availableCredit: "利用可能残高",
      pointsBalance: "ポイント残高",
      savedCoupons: "保存済みクーポン",
      pointRule: "お支払い済みの会員予約は、会員ランクに応じて3%から5%のポイントを獲得できます。過去のお支払い済み予約も一致すると自動加算されます。ポイント有効期限は180日です。",
      reviewRule: "確認済みのサービス評価により、特典残高がアカウントに追加されます。",
      cashRule: "ポイントは現金に交換できません。",
      couponTitle: "マイクーポン",
      couponBody: "プラスの残高特典はアカウントクーポンとして保存され、次回予約の残高として利用できます。",
      noCoupons: "保存済みクーポンはまだありません。確認済みのサービス評価で特典残高を受け取れます。",
      redeemTitle: "クーポンコードを入力",
      redeemBody: "クーポンコードを入力すると、特典金額がアカウント残高に追加されます。",
      redeemPlaceholder: "クーポンコードを入力",
      redeemButton: "交換する",
      redeeming: "交換中...",
      redeemSuccess: "クーポンを交換しました。残高がアカウントに追加されました。",
      redeemFailed: "このクーポンコードを交換できませんでした。",
      rewardHistory: "特典履歴",
      noHistory: "残高またはポイント履歴はまだありません。",
      accountCoupon: "アカウントクーポン",
      defaultLedgerType: "特典残高",
      defaultCreditNote: "次回ご利用用の特典残高です。",
      issued: "発行日",
      savedInBalance: "残高に保存済み",
      pointsUnit: "pt",
    },
    profile: {
      title: "プロフィール / セキュリティ",
      body: "お客様情報、領収書情報、アカウントパスワードを更新できます。",
      personalTitle: "個人 / 事業者情報",
      personalBody: "今後の予約、領収書、会員特典に使用されます。",
      name: "氏名",
      emailLoginId: "ログイン用メール",
      phone: "電話番号",
      countryRegion: "国 / 地域",
      preferredLanguage: "希望言語",
      receiptTitle: "領収書 / 請求情報",
      receiptBody: "領収書表示や今後のサポートに使用する任意情報です。",
      receiptName: "領収書宛名",
      receiptNamePlaceholder: "領収書に表示する宛名",
      companyName: "会社名",
      companyNamePlaceholder: "任意の会社名",
      billingAddress: "請求先住所",
      billingAddressPlaceholder: "任意の住所",
      save: "情報を保存",
      saving: "保存中...",
      saved: "プロフィールと領収書情報を更新しました。",
      saveFailed: "プロフィール情報を更新できませんでした。",
      changePassword: "パスワード変更",
      passwordBody: "現在のパスワードを使用して会員アカウントを保護します。",
      currentPassword: "現在のパスワード",
      newPassword: "新しいパスワード",
      confirmNewPassword: "新しいパスワード確認",
      updatePassword: "パスワードを更新",
      updating: "更新中...",
      passwordUpdated: "パスワードを更新しました。",
      passwordFailed: "パスワードを更新できませんでした。",
    },
    support: {
      title: "お問い合わせ",
      body: "予約、支払い、緊急のサービスに関するご相談はこちらから。",
      email: "メール",
      whatsapp: "WhatsApp",
      whatsappBody: "スタッフに連絡",
      ai: "AIコンシェルジュ",
      aiBody: "このページのチャットボタンをご利用ください",
    },
    detail: {
      title: "予約詳細",
    },
    system: {
      loading: "My Pageを読み込み中...",
      loadFailed: "My Pageを読み込めませんでした。",
      refresh: "My Pageを更新",
    },
  },
  zh: {
    guestCenter: "客人会员中心",
    signInTitle: "登录后查看预约和奖励",
    signInBody: "My Page 用于散客会员查看预约、付款、收据、余额和积分。",
    newBooking: "新预约",
    signOut: "退出登录",
    myPage: "我的页面",
    welcome: "欢迎",
    overview: {
      upcoming: "未出行预约",
      awaitingPayment: "待付款",
      availableCredit: "可用余额",
      points: "积分",
      memberStatus: "会员状态",
      active: "有效",
      pointsRate: "积分返还比例",
      cumulativeSpend: "累计消费金额",
      nextTier: "距离下一等级",
      topTier: "最高等级",
    },
    reservations: {
      title: "我的预约",
      body: "按服务日期和时间排序。请选择分类快速查找预约。",
      order: "按服务时间排序",
      year: "年度",
      allYears: "全部年度",
      month: "月份",
      allMonths: "全部月份",
      search: "搜索订单",
      searchPlaceholder: "姓名、日期、确认编号、客户代码、上车地或下车地",
      empty: "目前还没有绑定预约。请使用会员下单，或用历史预约相同的邮箱或手机号注册。",
      noMatch: "没有符合当前分类、月份或搜索条件的预约。",
    },
    reservationTabs: {
      pending_payment: "待付款",
      upcoming: "未出行",
      refund_aftercare: "退款/售后",
      pending_review: "待点评",
      all: "全部订单",
    },
    common: {
      reservation: "预约",
      privateRide: "私人接送",
      datePending: "日期待确认",
      paymentDatePending: "付款日期待确认",
      toBeConfirmed: "待确认",
      notSet: "未设置",
      amount: "金额",
      customerName: "客户名",
      customerCode: "客户代码",
      serviceDate: "服务日期",
      serviceType: "服务类型",
      pickupLocation: "上车地点",
      dropoffLocation: "下车地点",
      vehicleType: "车型",
      paymentStatus: "付款状态",
      reservationStatus: "预约状态",
      payNow: "立即付款",
      viewDetails: "查看详情",
      bookAgain: "再次预订",
      reviewService: "评价服务",
      downloadReceiptPdf: "下载收据 PDF",
      receiptAfterPayment: "付款完成后可下载收据",
      squarePayment: "Square 安全付款",
      afterPayment: "付款后",
      close: "关闭",
    },
    status: {
      refunded: "已退款",
      paid: "已付款",
      unpaid: "未付款",
      cancelled: "已取消",
      completed: "已完成",
      confirmed: "已确认",
      awaitingConfirmation: "等待确认",
    },
    serviceTypes: {
      privateCharter: "包车服务",
      airportPickup: "机场接机",
      airportDropoff: "机场送机",
      privateTransfer: "单程接送",
    },
    payments: {
      title: "付款记录 / 收据",
      body: "下载包含公司信息、税额和预约明细的正式 PDF 收据。",
      combinedTitle: "合并收据 PDF",
      combinedBody: "设置收据抬头，并选择一个或多个已付款预约合并开具。",
      receiptName: "收据抬头",
      receiptNamePlaceholder: "显示在收据上的抬头",
      selectedTotal: "已选合计",
      selectedCount: "已选择 {count} 个预约",
      downloadCombined: "下载合并 PDF",
      generating: "生成中...",
      selectAllPaid: "选择全部已付款",
      clear: "清除",
      noRecords: "暂无付款记录。",
      selectAtLeastOne: "请至少选择一个已付款预约。",
      generated: "收据 PDF 已生成。",
      failed: "无法生成收据 PDF。",
    },
    receiptGuide: {
      title: "收据 PDF 说明",
      headline: "为您的预约生成正式收据。",
      body: "可以为单个已付款预约生成收据，也可以选择多个已付款预约合并成一份 PDF。",
      before: "下载前确认",
      step1Title: "设置收据抬头",
      step1Body: "请输入需要显示在收据上的姓名或公司名称。",
      step2Title: "选择已付款预约",
      step2Body: "只有已付款预约可以包含在收据 PDF 中。",
      step3Title: "下载 PDF",
      step3Body: "PDF 会包含公司信息、税额、付款状态和预约明细。",
      includesTitle: "正式 PDF 包含",
      includes: [
        "领收书标题和收据编号",
        "客户设置的收据抬头",
        "湘南国際株式会社发行方信息和印章",
        "登记编号和消费税金额",
        "Square 全额付款状态",
      ],
    },
    rewards: {
      title: "我的优惠券 / 奖励钱包",
      body: "账户中的优惠券、余额和积分都会保存，可用于下次用车。",
      availableCredit: "可用余额",
      pointsBalance: "积分余额",
      savedCoupons: "已保存优惠券",
      pointRule: "会员预约付款成功后，可按会员等级获得实付金额3%至5%的积分。匹配到的历史已付款预约也会自动补发，积分有效期180天。",
      reviewRule: "完成验证后的服务评价，可将奖励余额加入账户。",
      cashRule: "积分不可兑换现金。",
      couponTitle: "我的优惠券",
      couponBody: "正向余额奖励会保存为账户优惠券，可在之后的散客预约中作为余额使用。",
      noCoupons: "暂无已保存优惠券。完成验证后的服务评价可领取奖励余额。",
      redeemTitle: "输入兑换码",
      redeemBody: "输入优惠券兑换码后，奖励金额会加入账户余额。",
      redeemPlaceholder: "请输入兑换码",
      redeemButton: "兑换",
      redeeming: "兑换中...",
      redeemSuccess: "兑换成功，余额已加入您的账户。",
      redeemFailed: "无法兑换这个优惠券代码。",
      rewardHistory: "奖励记录",
      noHistory: "暂无余额或积分记录。",
      accountCoupon: "账户优惠券",
      defaultLedgerType: "奖励余额",
      defaultCreditNote: "用于下次用车的奖励余额。",
      issued: "发行日期",
      savedInBalance: "已保存到账户余额",
      pointsUnit: "积分",
    },
    profile: {
      title: "个人资料 / 安全设置",
      body: "更新客户信息、收据信息和账户密码。",
      personalTitle: "个人 / 商用信息",
      personalBody: "这些信息会用于之后的预约、收据和会员奖励。",
      name: "姓名",
      emailLoginId: "登录邮箱",
      phone: "电话",
      countryRegion: "国家 / 地区",
      preferredLanguage: "首选语言",
      receiptTitle: "收据 / 开票信息",
      receiptBody: "用于收据显示和之后客服支持的选填信息。",
      receiptName: "收据抬头",
      receiptNamePlaceholder: "显示在收据上的抬头",
      companyName: "公司名称",
      companyNamePlaceholder: "选填公司名称",
      billingAddress: "账单地址",
      billingAddressPlaceholder: "选填地址",
      save: "保存信息",
      saving: "保存中...",
      saved: "资料和收据信息已更新。",
      saveFailed: "无法更新资料信息。",
      changePassword: "修改密码",
      passwordBody: "使用当前密码保护您的会员账户。",
      currentPassword: "当前密码",
      newPassword: "新密码",
      confirmNewPassword: "确认新密码",
      updatePassword: "更新密码",
      updating: "更新中...",
      passwordUpdated: "密码已更新。",
      passwordFailed: "无法更新密码。",
    },
    support: {
      title: "联系客服",
      body: "预约问题、付款协助或紧急服务问题都可以联系我们。",
      email: "邮箱",
      whatsapp: "WhatsApp",
      whatsappBody: "联系工作人员",
      ai: "AI 智能客服",
      aiBody: "请使用本页面的聊天按钮",
    },
    detail: {
      title: "预约详情",
    },
    system: {
      loading: "正在加载 My Page...",
      loadFailed: "无法加载 My Page。",
      refresh: "刷新 My Page",
    },
  },
  zhHant: {
    guestCenter: "客人會員中心",
    signInTitle: "登入後查看預約和獎勵",
    signInBody: "My Page 用於散客會員查看預約、付款、收據、餘額和積分。",
    newBooking: "新預約",
    signOut: "登出",
    myPage: "我的頁面",
    welcome: "歡迎",
    overview: {
      upcoming: "未出行預約",
      awaitingPayment: "待付款",
      availableCredit: "可用餘額",
      points: "積分",
      memberStatus: "會員狀態",
      active: "有效",
      pointsRate: "積分返還比例",
      cumulativeSpend: "累計消費金額",
      nextTier: "距離下一等級",
      topTier: "最高等級",
    },
    reservations: {
      title: "我的預約",
      body: "按服務日期和時間排序。請選擇分類快速查找預約。",
      order: "按服務時間排序",
      year: "年度",
      allYears: "全部年度",
      month: "月份",
      allMonths: "全部月份",
      search: "搜尋訂單",
      searchPlaceholder: "姓名、日期、確認編號、客戶代碼、上車地或下車地",
      empty: "目前還沒有綁定預約。請使用會員下單，或用歷史預約相同的電子郵件或手機號註冊。",
      noMatch: "沒有符合目前分類、月份或搜尋條件的預約。",
    },
    reservationTabs: {
      pending_payment: "待付款",
      upcoming: "未出行",
      refund_aftercare: "退款／售後",
      pending_review: "待評價",
      all: "全部訂單",
    },
    common: {
      reservation: "預約",
      privateRide: "私人接送",
      datePending: "日期待確認",
      paymentDatePending: "付款日期待確認",
      toBeConfirmed: "待確認",
      notSet: "未設定",
      amount: "金額",
      customerName: "客戶名",
      customerCode: "客戶代碼",
      serviceDate: "服務日期",
      serviceType: "服務類型",
      pickupLocation: "上車地點",
      dropoffLocation: "下車地點",
      vehicleType: "車型",
      paymentStatus: "付款狀態",
      reservationStatus: "預約狀態",
      payNow: "立即付款",
      viewDetails: "查看詳情",
      bookAgain: "再次預訂",
      reviewService: "評價服務",
      downloadReceiptPdf: "下載收據 PDF",
      receiptAfterPayment: "付款完成後可下載收據",
      squarePayment: "Square 安全付款",
      afterPayment: "付款後",
      close: "關閉",
    },
    status: {
      refunded: "已退款",
      paid: "已付款",
      unpaid: "未付款",
      cancelled: "已取消",
      completed: "已完成",
      confirmed: "已確認",
      awaitingConfirmation: "等待確認",
    },
    serviceTypes: {
      privateCharter: "包車服務",
      airportPickup: "機場接機",
      airportDropoff: "機場送機",
      privateTransfer: "單程接送",
    },
    payments: {
      title: "付款記錄 / 收據",
      body: "下載包含公司資訊、稅額和預約明細的正式 PDF 收據。",
      combinedTitle: "合併收據 PDF",
      combinedBody: "設定收據抬頭，並選擇一個或多個已付款預約合併開具。",
      receiptName: "收據抬頭",
      receiptNamePlaceholder: "顯示在收據上的抬頭",
      selectedTotal: "已選合計",
      selectedCount: "已選擇 {count} 個預約",
      downloadCombined: "下載合併 PDF",
      generating: "生成中...",
      selectAllPaid: "選擇全部已付款",
      clear: "清除",
      noRecords: "暫無付款記錄。",
      selectAtLeastOne: "請至少選擇一個已付款預約。",
      generated: "收據 PDF 已生成。",
      failed: "無法生成收據 PDF。",
    },
    receiptGuide: {
      title: "收據 PDF 說明",
      headline: "為您的預約生成正式收據。",
      body: "可以為單個已付款預約生成收據，也可以選擇多個已付款預約合併成一份 PDF。",
      before: "下載前確認",
      step1Title: "設定收據抬頭",
      step1Body: "請輸入需要顯示在收據上的姓名或公司名稱。",
      step2Title: "選擇已付款預約",
      step2Body: "只有已付款預約可以包含在收據 PDF 中。",
      step3Title: "下載 PDF",
      step3Body: "PDF 會包含公司資訊、稅額、付款狀態和預約明細。",
      includesTitle: "正式 PDF 包含",
      includes: [
        "領收書標題和收據編號",
        "客戶設定的收據抬頭",
        "湘南国際株式会社發行方資訊和印章",
        "登記編號和消費稅金額",
        "Square 全額付款狀態",
      ],
    },
    rewards: {
      title: "我的優惠券 / 獎勵錢包",
      body: "帳戶中的優惠券、餘額和積分都會保存，可用於下次用車。",
      availableCredit: "可用餘額",
      pointsBalance: "積分餘額",
      savedCoupons: "已保存優惠券",
      pointRule: "會員預約付款成功後，可按會員等級獲得實付金額3%至5%的積分。配對到的歷史已付款預約也會自動補發，積分有效期180天。",
      reviewRule: "完成驗證後的服務評價，可將獎勵餘額加入帳戶。",
      cashRule: "積分不可兌換現金。",
      couponTitle: "我的優惠券",
      couponBody: "正向餘額獎勵會保存為帳戶優惠券，可在之後的散客預約中作為餘額使用。",
      noCoupons: "暫無已保存優惠券。完成驗證後的服務評價可領取獎勵餘額。",
      redeemTitle: "輸入兌換碼",
      redeemBody: "輸入優惠券兌換碼後，獎勵金額會加入帳戶餘額。",
      redeemPlaceholder: "請輸入兌換碼",
      redeemButton: "兌換",
      redeeming: "兌換中...",
      redeemSuccess: "兌換成功，餘額已加入您的帳戶。",
      redeemFailed: "無法兌換這個優惠券代碼。",
      rewardHistory: "獎勵記錄",
      noHistory: "暫無餘額或積分記錄。",
      accountCoupon: "帳戶優惠券",
      defaultLedgerType: "獎勵餘額",
      defaultCreditNote: "用於下次用車的獎勵餘額。",
      issued: "發行日期",
      savedInBalance: "已保存到帳戶餘額",
      pointsUnit: "積分",
    },
    profile: {
      title: "個人資料 / 安全設定",
      body: "更新客戶資訊、收據資訊和帳戶密碼。",
      personalTitle: "個人 / 商用資訊",
      personalBody: "這些資訊會用於之後的預約、收據和會員獎勵。",
      name: "姓名",
      emailLoginId: "登入信箱",
      phone: "電話",
      countryRegion: "國家 / 地區",
      preferredLanguage: "首選語言",
      receiptTitle: "收據 / 開票資訊",
      receiptBody: "用於收據顯示和之後客服支援的選填資訊。",
      receiptName: "收據抬頭",
      receiptNamePlaceholder: "顯示在收據上的抬頭",
      companyName: "公司名稱",
      companyNamePlaceholder: "選填公司名稱",
      billingAddress: "帳單地址",
      billingAddressPlaceholder: "選填地址",
      save: "保存資訊",
      saving: "保存中...",
      saved: "資料和收據資訊已更新。",
      saveFailed: "無法更新資料資訊。",
      changePassword: "修改密碼",
      passwordBody: "使用目前密碼保護您的會員帳戶。",
      currentPassword: "目前密碼",
      newPassword: "新密碼",
      confirmNewPassword: "確認新密碼",
      updatePassword: "更新密碼",
      updating: "更新中...",
      passwordUpdated: "密碼已更新。",
      passwordFailed: "無法更新密碼。",
    },
    support: {
      title: "聯絡客服",
      body: "預約問題、付款協助或緊急服務問題都可以聯絡我們。",
      email: "信箱",
      whatsapp: "WhatsApp",
      whatsappBody: "聯絡工作人員",
      ai: "AI 智能客服",
      aiBody: "請使用本頁面的聊天按鈕",
    },
    detail: {
      title: "預約詳情",
    },
    system: {
      loading: "正在載入 My Page...",
      loadFailed: "無法載入 My Page。",
      refresh: "重新整理 My Page",
    },
  },
};

function formatJPY(value) {
  return `JPY ${Number(value || 0).toLocaleString("ja-JP")}`;
}

function fillTemplate(text, values = {}) {
  return String(text || "").replace(/\{(\w+)\}/g, (_, key) => values[key] ?? "");
}

function isPaid(order) {
  const status = String(order.square_payment_status || order.payment_status || "").toLowerCase();
  return ["paid", "completed", "success"].some((item) => status.includes(item));
}

function isRefunded(order) {
  const status = `${order.status || ""} ${order.square_payment_status || ""}`.toLowerCase();
  return status.includes("refund");
}

function paymentStatusLabel(order, copy = memberPageCopy.en) {
  if (isRefunded(order)) return copy.status.refunded;
  if (isPaid(order)) return copy.status.paid;
  return copy.status.unpaid;
}

function reservationStatusLabel(order, copy = memberPageCopy.en) {
  const status = String(order.status || "").toLowerCase();
  if (status.includes("cancel")) return copy.status.cancelled;
  if (status.includes("complete")) return copy.status.completed;
  if (status.includes("confirm")) return copy.status.confirmed;
  if (status.includes("pending")) return copy.status.awaitingConfirmation;
  return order.status || copy.status.awaitingConfirmation;
}

function formatServiceType(order, copy = memberPageCopy.en) {
  if (String(order.service_type || "").toLowerCase() === "charter") return copy.serviceTypes.privateCharter;
  const subtype = String(order.transfer_subtype || "").toLowerCase();
  if (subtype.includes("pickup")) return copy.serviceTypes.airportPickup;
  if (subtype.includes("dropoff")) return copy.serviceTypes.airportDropoff;
  return copy.serviceTypes.privateTransfer;
}

function isUpcoming(order) {
  const status = String(order.status || "").toLowerCase();
  if (status.includes("cancel") || status.includes("complete")) return false;
  if (!order.departure_date) return true;
  return String(order.departure_date) >= getTokyoDateKey();
}

function getOrderServiceTimestamp(order) {
  if (!order.departure_date) return Number.MAX_SAFE_INTEGER;
  const pickupTime = String(order.pickup_time || "00:00").slice(0, 5);
  const serviceDate = `${order.departure_date}T${pickupTime || "00:00"}:00+09:00`;
  const timestamp = new Date(serviceDate).getTime();
  return Number.isFinite(timestamp) ? timestamp : Number.MAX_SAFE_INTEGER;
}

function sortOrdersByServiceTime(orders) {
  return [...orders].sort((a, b) => {
    const serviceDiff = getOrderServiceTimestamp(a) - getOrderServiceTimestamp(b);
    if (serviceDiff !== 0) return serviceDiff;
    return String(b.created_at || "").localeCompare(String(a.created_at || ""));
  });
}

function isCancelledOrRefundFlow(order) {
  const status = `${order.status || ""} ${order.payment_status || ""} ${order.square_payment_status || ""}`.toLowerCase();
  return status.includes("cancel") || status.includes("refund");
}

function isCompletedOrder(order) {
  return String(order.status || "").toLowerCase().includes("complete");
}

function needsReview(order) {
  if (!isCompletedOrder(order) || isCancelledOrRefundFlow(order)) return false;
  if (!getReviewLink(order)) return false;
  return !order.review_submitted && !order.service_review_submitted && order.review_status !== "submitted";
}

function getReviewLink(order) {
  return order.review_invitation_url || order.retail_review_invitation_url || order.review_url || "";
}

function normalizeSearchText(value) {
  return String(value || "").toLowerCase().replace(/\s+/g, "");
}

function orderMatchesSearch(order, query) {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) return true;
  return [
    order.customer_name,
    order.customer_code,
    order.departure_date,
    order.confirmation_code,
    order.order_no,
    order.pickup_location,
    order.destination,
    order.route,
  ].some((value) => normalizeSearchText(value).includes(normalizedQuery));
}

function orderMatchesMonth(order, year, month) {
  if (year === "all" && month === "all") return true;
  const date = String(order.departure_date || "");
  if (!date) return false;
  const [orderYear, orderMonth] = date.split("-");
  if (year !== "all" && orderYear !== year) return false;
  if (month !== "all" && orderMonth !== month) return false;
  return true;
}

const reservationFilterTabs = [
  {
    key: "pending_payment",
    match: (order) => !isPaid(order) && !isCancelledOrRefundFlow(order),
  },
  {
    key: "upcoming",
    match: (order) => isUpcoming(order) && !isCancelledOrRefundFlow(order),
  },
  {
    key: "refund_aftercare",
    match: isCancelledOrRefundFlow,
  },
  {
    key: "pending_review",
    match: needsReview,
  },
  {
    key: "all",
    match: () => true,
  },
];

async function downloadReceipt(order, token) {
  const response = await axios.get(`${API}/public-booking/member/orders/${encodeURIComponent(order.id)}/receipt.pdf`, {
    headers: { "X-Retail-Member-Token": token },
    responseType: "blob",
  });
  const disposition = response.headers?.["content-disposition"] || "";
  const filenameMatch = disposition.match(/filename\*=UTF-8''([^;]+)/);
  const filename = filenameMatch
    ? decodeURIComponent(filenameMatch[1])
    : `領収書_${order.confirmation_code || order.order_no || "booking"}.pdf`;
  const blob = new Blob([response.data], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

async function downloadCombinedReceipt({ orderIds, receiptName, token }) {
  const response = await axios.post(
    `${API}/public-booking/member/receipts/combined.pdf`,
    {
      order_ids: orderIds,
      receipt_name: receiptName,
    },
    {
      headers: { "X-Retail-Member-Token": token },
      responseType: "blob",
    },
  );
  const disposition = response.headers?.["content-disposition"] || "";
  const filenameMatch = disposition.match(/filename\*=UTF-8''([^;]+)/);
  const filename = filenameMatch
    ? decodeURIComponent(filenameMatch[1])
    : `領収書_合計${orderIds.length}件.pdf`;
  const blob = new Blob([response.data], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export default function PublicMemberMyPage() {
  const [lang, setLang] = useState(() => localStorage.getItem("public_booking_lang") || "en");
  const [token, setToken] = useState(() => localStorage.getItem(retailTokenKey) || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(Boolean(token));
  const [error, setError] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [reservationFilter, setReservationFilter] = useState("pending_payment");
  const [profileMessage, setProfileMessage] = useState("");
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileForm, setProfileForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    country_region: "Japan",
    preferred_language: localStorage.getItem("public_booking_lang") || "en",
    receipt_name: "",
    company_name: "",
    billing_address: "",
  });
  const copy = memberPageCopy[lang] || memberPageCopy.en;
  const guestPresentation = memberGuestPresentationCopy[lang] || memberGuestPresentationCopy.en;

  const loadMyPage = useCallback(async (nextToken = token) => {
    if (!nextToken) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${API}/public-booking/member/mypage`, {
        headers: { "X-Retail-Member-Token": nextToken },
      });
      setData(response.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem(retailTokenKey);
        setToken("");
        setData(null);
      }
      setError(err.response?.data?.detail || (memberPageCopy[lang] || memberPageCopy.en).system.loadFailed);
    } finally {
      setLoading(false);
    }
  }, [token, lang]);

  useEffect(() => {
    loadMyPage(token);
  }, [loadMyPage, token]);

  useEffect(() => {
    const member = data?.member;
    if (!member) return;
    const nextLang = member.preferred_language || localStorage.getItem("public_booking_lang") || "en";
    setLang(nextLang);
    setProfileForm({
      full_name: member.full_name || "",
      email: member.email || "",
      phone: member.phone || "",
      country_region: member.country_region || "Japan",
      preferred_language: nextLang,
      receipt_name: member.receipt_name || "",
      company_name: member.company_name || "",
      billing_address: member.billing_address || "",
    });
  }, [data?.member]);

  const handleMemberChange = (member, nextToken) => {
    const activeToken = nextToken || localStorage.getItem(retailTokenKey) || "";
    setToken(activeToken);
    setData((prev) => ({ ...(prev || {}), member }));
    loadMyPage(activeToken);
  };

  const logout = () => {
    localStorage.removeItem(retailTokenKey);
    setToken("");
    setData(null);
  };

  const handleLanguageChange = (value) => {
    setLang(value);
    localStorage.setItem("public_booking_lang", value);
    setProfileForm((prev) => ({ ...prev, preferred_language: value }));
  };

  const member = data?.member;
  const orders = useMemo(() => data?.orders || [], [data?.orders]);
  const ledger = useMemo(() => data?.ledger || [], [data?.ledger]);
  const overview = useMemo(() => {
    const upcoming = orders.filter(isUpcoming).length;
    const unpaid = orders.filter((order) => !isPaid(order) && !isRefunded(order)).length;
    return {
      upcoming,
      unpaid,
      credit: Number(member?.credit_balance_jpy || data?.summary?.credit_balance_jpy || 0),
      points: Number(member?.points_balance || data?.summary?.points_balance || 0),
      membershipTierLabel: member?.membership_tier_label || data?.summary?.membership_tier_label || "Regular Member",
      pointsRate: Number(member?.points_rate || data?.summary?.points_rate || 0.03),
      cumulativeSpend: Number(member?.cumulative_spend_jpy || data?.summary?.cumulative_spend_jpy || 0),
      amountToNextTier: Number(member?.amount_to_next_tier_jpy || data?.summary?.amount_to_next_tier_jpy || 0),
    };
  }, [orders, member, data]);

  const paymentRecords = orders.filter((order) => Number(order.amount_jpy || 0) > 0);

  const openReservationBucket = (filterKey) => {
    setReservationFilter(filterKey);
    setTimeout(() => {
      document.getElementById("my-reservations")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  };

  const saveProfileUi = async () => {
    if (!token) return;
    setProfileSaving(true);
    setProfileMessage("");
    try {
      const response = await axios.patch(`${API}/public-booking/member/profile`, profileForm, {
        headers: { "X-Retail-Member-Token": token },
      });
      localStorage.setItem("public_booking_lang", profileForm.preferred_language);
      setLang(profileForm.preferred_language);
      if (response.data?.member) {
        setData((prev) => ({ ...(prev || {}), member: response.data.member, binding_summary: response.data.binding_summary }));
      }
      setProfileMessage(copy.profile.saved);
    } catch (err) {
      setProfileMessage(err.response?.data?.detail || copy.profile.saveFailed);
    } finally {
      setProfileSaving(false);
    }
  };

  return (
    <main className="relative min-h-screen overflow-hidden bg-[#030303] px-4 py-5 text-white">
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute left-1/2 top-[-420px] h-[760px] w-[1000px] -translate-x-1/2 rounded-full bg-[#b78a3d]/[0.09] blur-[150px]" />
        <div className="absolute bottom-[-360px] right-[-180px] h-[620px] w-[620px] rounded-full bg-[#d8b769]/[0.055] blur-[130px]" />
      </div>
      <div className="relative mx-auto max-w-6xl">
        <Header token={token} onLogout={logout} lang={lang} onLanguageChange={handleLanguageChange} copy={copy} />

        {!token ? (
          <section className="mx-auto grid min-h-[calc(100vh-130px)] max-w-[1120px] items-center gap-5 py-7 lg:grid-cols-[minmax(0,1.24fr)_minmax(390px,0.76fr)] lg:items-stretch lg:gap-6 lg:py-9" data-testid="premium-member-login">
            <MemberGuestHero presentation={guestPresentation} />
            <RetailMemberAuthPanel onMemberChange={handleMemberChange} myPage language={lang} />
          </section>
        ) : (
          <section className="mt-7 space-y-5">
            {loading && <div className="rounded-[30px] border border-[#d8b769]/20 bg-[#0d0c0a] p-7 text-white/70">{copy.system.loading}</div>}
            {error && <div className="rounded-3xl border border-red-400/25 bg-red-400/10 p-4 text-sm text-red-100">{error}</div>}
            {member && (
              <>
                <Overview member={member} overview={overview} onRefresh={() => loadMyPage(token)} onOpenReservations={openReservationBucket} copy={copy} />
                <Reservations orders={orders} activeFilter={reservationFilter} setActiveFilter={setReservationFilter} onSelect={setSelectedOrder} copy={copy} />
                <Payments orders={paymentRecords} copy={copy} />
                <Rewards overview={overview} ledger={ledger} copy={copy} onRedeemed={() => loadMyPage(token)} />
                <Profile form={profileForm} setForm={setProfileForm} onSave={saveProfileUi} message={profileMessage} saving={profileSaving} token={token} copy={copy} />
                <Support copy={copy} />
              </>
            )}
          </section>
        )}
      </div>
      {selectedOrder && <OrderDetailModal order={selectedOrder} onClose={() => setSelectedOrder(null)} copy={copy} />}
    </main>
  );
}

function MemberGuestHero({ presentation }) {
  const benefitIcons = [Star, WalletCards, ShieldCheck];

  return (
    <section className="group relative min-h-[510px] overflow-hidden rounded-[30px] border border-[#d7b76b]/24 bg-[#090806] shadow-[0_38px_100px_rgba(0,0,0,0.48)] sm:min-h-[590px] sm:rounded-[34px] lg:h-full" data-testid="premium-member-hero">
      <img
        src="/email-assets/booking-confirmation-hero.jpg"
        alt={presentation.imageAlt}
        className="absolute inset-0 h-full w-full object-cover object-center opacity-90 transition duration-1000 ease-out group-hover:scale-[1.02] lg:object-[58%_center]"
      />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.06)_0%,rgba(0,0,0,0.18)_34%,rgba(3,3,3,0.92)_76%,#050505_100%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(0,0,0,0.08),transparent_58%)]" />
      <div className="absolute left-7 top-7 inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#efd58f] sm:left-9 sm:top-9">
        <span className="h-1.5 w-1.5 rounded-full bg-[#e2bd67] shadow-[0_0_12px_rgba(226,189,103,0.9)]" />
        Shonan Member
      </div>

      <div className="absolute inset-x-0 bottom-0 p-6 sm:p-9 lg:p-10">
        <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#d8b769] sm:text-[11px]">
          {presentation.eyebrow}
        </p>
        <h1 className={`mt-3 max-w-[650px] font-serif font-normal tracking-[-0.03em] text-[#f6f1e6] ${presentation.compactTitle ? "text-[22px] leading-[1.18] sm:text-[36px] sm:leading-[1.12] lg:text-[42px]" : "text-[31px] leading-[1.07] sm:text-[48px] sm:leading-[1.03] lg:text-[54px]"}`}>
          {presentation.titleLines.map((line) => <span key={line} className="block whitespace-nowrap">{line}</span>)}
        </h1>
        <p className="mt-3 max-w-[570px] text-[12px] leading-6 text-white/68 sm:mt-4 sm:text-[15px] sm:leading-7">
          {presentation.body}
        </p>

        <div className="mt-5 grid grid-cols-3 gap-px overflow-hidden rounded-[17px] border border-white/10 bg-white/10 backdrop-blur-xl sm:mt-7 sm:rounded-[20px]">
          {presentation.benefits.map(([title, body], index) => {
            const Icon = benefitIcons[index];
            return (
              <div key={title} className="min-w-0 bg-[#090806]/82 p-3 transition hover:bg-[#171208]/88 sm:p-4">
                <Icon className="h-4 w-4 text-[#d8b769]" strokeWidth={1.6} />
                <p className="mt-2 text-[9px] font-semibold leading-4 text-[#f2e7cb] sm:mt-3 sm:text-xs">{title}</p>
                <p className="mt-1.5 hidden text-[10px] leading-[1.6] text-white/47 sm:block">{body}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Header({ token, onLogout, lang, onLanguageChange, copy }) {
  return (
    <header className="relative z-[110] flex min-h-[70px] flex-col justify-center gap-4 border-b border-white/10 py-3 sm:flex-row sm:items-center sm:justify-between">
      <PublicBrandMark href="/book" fluidMobile />
      <div className="flex flex-wrap gap-2">
        <PublicLanguageSwitcher
          options={languageOptions}
          activeCode={lang}
          onChange={(option) => onLanguageChange(option.code)}
          mobileMenuAlign="left"
        />
        <a href="/book" className="inline-flex items-center justify-center rounded-full border border-[#d8b769]/35 bg-[#d8b769]/10 px-4 py-2 text-xs font-bold text-[#f3d98b] transition hover:bg-[#d8b769] hover:text-black">
          {copy.newBooking}
          <ArrowRight className="ml-2 h-3.5 w-3.5" />
        </a>
        {token && (
          <button type="button" onClick={onLogout} className="inline-flex items-center justify-center rounded-full border border-white/10 bg-white/[0.04] px-4 py-2 text-xs font-semibold text-white/65 transition hover:text-white">
            <LogOut className="mr-2 h-3.5 w-3.5" />
            {copy.signOut}
          </button>
        )}
      </div>
    </header>
  );
}

function Overview({ member, overview, onRefresh, onOpenReservations, copy }) {
  return (
    <section className="rounded-[34px] border border-[#d8b769]/25 bg-[#0d0c0a] p-5 shadow-2xl shadow-black/35 md:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#d8b769] text-black">
            <UserRound className="h-6 w-6" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#d8b769]">{copy.myPage}</p>
            <h1 className="mt-2 break-words text-3xl font-semibold">{copy.welcome} {member.full_name || member.email}</h1>
            <p className="mt-2 break-words text-sm text-white/55">{member.email}</p>
          </div>
        </div>
        <button type="button" onClick={onRefresh} className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-white/60 transition hover:text-[#f3d98b]" aria-label={copy.system.refresh}>
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>
      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard icon={<CalendarClock className="h-5 w-5" />} label={copy.overview.upcoming} value={overview.upcoming} onClick={() => onOpenReservations("upcoming")} />
        <MetricCard icon={<CreditCard className="h-5 w-5" />} label={copy.overview.awaitingPayment} value={overview.unpaid} onClick={() => onOpenReservations("pending_payment")} />
        <MetricCard icon={<WalletCards className="h-5 w-5" />} label={copy.overview.availableCredit} value={formatJPY(overview.credit)} />
        <MetricCard icon={<Coins className="h-5 w-5" />} label={copy.overview.points} value={overview.points.toLocaleString("ja-JP")} />
        <MetricCard icon={<CheckCircle2 className="h-5 w-5" />} label={copy.overview.memberStatus} value={overview.membershipTierLabel} />
        <MetricCard icon={<Coins className="h-5 w-5" />} label={copy.overview.pointsRate} value={`${Math.round(overview.pointsRate * 100)}%`} />
        <MetricCard icon={<WalletCards className="h-5 w-5" />} label={copy.overview.cumulativeSpend} value={formatJPY(overview.cumulativeSpend)} />
        <MetricCard icon={<ArrowRight className="h-5 w-5" />} label={copy.overview.nextTier} value={overview.amountToNextTier > 0 ? formatJPY(overview.amountToNextTier) : copy.overview.topTier} />
      </div>
    </section>
  );
}

function Reservations({ orders, activeFilter, setActiveFilter, onSelect, copy }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedYear, setSelectedYear] = useState("all");
  const [selectedMonth, setSelectedMonth] = useState("all");
  const sortedOrders = useMemo(() => sortOrdersByServiceTime(orders), [orders]);
  const yearOptions = useMemo(() => {
    const years = new Set(sortedOrders.map((order) => String(order.departure_date || "").slice(0, 4)).filter(Boolean));
    return [...years].sort((a, b) => b.localeCompare(a));
  }, [sortedOrders]);
  const controlFilteredOrders = useMemo(
    () => sortedOrders.filter((order) => orderMatchesMonth(order, selectedYear, selectedMonth) && orderMatchesSearch(order, searchQuery)),
    [sortedOrders, selectedYear, selectedMonth, searchQuery],
  );
  const tabItems = useMemo(
    () => reservationFilterTabs.map((tab) => ({
      ...tab,
      label: copy.reservationTabs[tab.key] || tab.key,
      count: controlFilteredOrders.filter(tab.match).length,
    })),
    [controlFilteredOrders, copy],
  );
  const activeTab = tabItems.find((tab) => tab.key === activeFilter) || tabItems[0];
  const visibleOrders = useMemo(
    () => controlFilteredOrders.filter(activeTab.match),
    [controlFilteredOrders, activeTab],
  );

  return (
    <section id="my-reservations" className="scroll-mt-6 rounded-[34px] border border-white/10 bg-[#0d0c0a] p-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <SectionHeader title={copy.reservations.title} body={copy.reservations.body} />
        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white/35">{copy.reservations.order}</span>
      </div>
      <div className="-mx-1 mt-4 flex gap-2 overflow-x-auto px-1 pb-1">
        {tabItems.map((tab) => {
          const selected = activeFilter === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveFilter(tab.key)}
              className={`inline-flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition ${
                selected
                  ? "border-[#d8b769] bg-[#d8b769] text-black shadow-lg shadow-[#d8b769]/20"
                  : "border-white/10 bg-black/25 text-white/68 hover:border-[#d8b769]/45 hover:text-[#f3d98b]"
              }`}
            >
              <span>{tab.label}</span>
              <span className={`rounded-full px-2 py-0.5 text-xs ${selected ? "bg-black/12 text-black" : "bg-white/[0.07] text-white/55"}`}>
                {tab.count}
              </span>
            </button>
          );
        })}
      </div>
      <div className="mt-4 grid gap-3 lg:grid-cols-[170px_150px_minmax(0,1fr)]">
        <label className="grid gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-white/42">
          {copy.reservations.year}
          <select className="rounded-2xl border border-white/10 bg-white px-4 py-3 text-sm text-black outline-none" value={selectedYear} onChange={(event) => setSelectedYear(event.target.value)}>
            <option value="all">{copy.reservations.allYears}</option>
            {yearOptions.map((year) => <option key={year} value={year}>{year}</option>)}
          </select>
        </label>
        <label className="grid gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-white/42">
          {copy.reservations.month}
          <select className="rounded-2xl border border-white/10 bg-white px-4 py-3 text-sm text-black outline-none" value={selectedMonth} onChange={(event) => setSelectedMonth(event.target.value)}>
            <option value="all">{copy.reservations.allMonths}</option>
            {Array.from({ length: 12 }, (_, index) => {
              const value = String(index + 1).padStart(2, "0");
              return <option key={value} value={value}>{value}</option>;
            })}
          </select>
        </label>
        <label className="grid gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-white/42">
          {copy.reservations.search}
          <span className="flex items-center gap-2 rounded-2xl border border-white/10 bg-white px-4 py-3 text-black">
            <Search className="h-4 w-4 text-black/35" />
            <input
              className="min-w-0 flex-1 bg-transparent text-sm outline-none"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder={copy.reservations.searchPlaceholder}
            />
          </span>
        </label>
      </div>
      <div className="mt-4 grid gap-3">
        {orders.length === 0 ? (
          <EmptyState text={copy.reservations.empty} />
        ) : visibleOrders.length === 0 ? (
          <EmptyState text={copy.reservations.noMatch} />
        ) : (
          visibleOrders.map((order) => <ReservationCard key={order.id} order={order} token={localStorage.getItem(retailTokenKey) || ""} onSelect={onSelect} copy={copy} />)
        )}
      </div>
    </section>
  );
}

function ReservationCard({ order, token, onSelect, copy }) {
  const paid = isPaid(order);
  const completed = String(order.status || "").toLowerCase().includes("complete");
  const reviewLink = getReviewLink(order);
  return (
    <article className="rounded-3xl border border-white/10 bg-black/30 p-4">
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_220px]">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-white/[0.06] px-3 py-1 text-xs font-semibold text-white/65">{order.confirmation_code || order.order_no || copy.common.reservation}</span>
            <StatusPill label={reservationStatusLabel(order, copy)} tone={completed ? "green" : "gold"} />
            <StatusPill label={paymentStatusLabel(order, copy)} tone={paid ? "green" : isRefunded(order) ? "muted" : "red"} />
          </div>
          <h3 className="mt-3 break-words text-lg font-semibold">{order.route || copy.common.privateRide}</h3>
          <div className="mt-3 grid gap-2 text-sm text-white/62 sm:grid-cols-2">
            <InfoLine label={copy.common.customerName} value={order.customer_name || copy.common.toBeConfirmed} />
            <InfoLine label={copy.common.customerCode} value={order.customer_code || copy.common.notSet} />
            <InfoLine label={copy.common.serviceDate} value={`${order.departure_date || copy.common.datePending} ${order.pickup_time || ""}`} />
            <InfoLine label={copy.common.serviceType} value={formatServiceType(order, copy)} />
            <InfoLine label={copy.common.pickupLocation} value={order.pickup_location || copy.common.toBeConfirmed} />
            <InfoLine label={copy.common.dropoffLocation} value={order.destination || copy.common.toBeConfirmed} />
            <InfoLine label={copy.common.vehicleType} value={vehicleNames[order.vehicle_type] || order.vehicle_type || copy.common.toBeConfirmed} />
            <InfoLine label={copy.common.paymentStatus} value={paymentStatusLabel(order, copy)} />
          </div>
        </div>
        <div className="flex flex-col justify-between gap-3 rounded-3xl border border-[#d8b769]/20 bg-[#d8b769]/10 p-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/40">{copy.common.amount}</p>
            <p className="mt-1 text-2xl font-semibold text-[#f3d98b]">{formatJPY(order.amount_jpy)}</p>
          </div>
          <div className="grid gap-2">
            <ActionButton onClick={() => onSelect(order)} label={copy.common.viewDetails} />
            {!paid && order.payment_url && <ActionLink href={order.payment_url} label={copy.common.payNow} icon={<CreditCard className="h-3.5 w-3.5" />} />}
            {completed && reviewLink && needsReview(order) && <ActionLink href={reviewLink} label={copy.common.reviewService} icon={<Star className="h-3.5 w-3.5" />} />}
            <ActionLink href="/book" label={copy.common.bookAgain} icon={<Repeat2 className="h-3.5 w-3.5" />} />
            {paid ? (
              <ActionButton onClick={() => downloadReceipt(order, token)} label={copy.common.downloadReceiptPdf} icon={<Download className="h-3.5 w-3.5" />} />
            ) : (
              <span className="rounded-2xl border border-white/10 bg-white/[0.03] px-3 py-2 text-center text-xs font-semibold text-white/38">{copy.common.receiptAfterPayment}</span>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

function Payments({ orders, copy }) {
  const [selectedReceiptOrderIds, setSelectedReceiptOrderIds] = useState([]);
  const [receiptName, setReceiptName] = useState("");
  const [receiptMessage, setReceiptMessage] = useState("");
  const [receiptLoading, setReceiptLoading] = useState(false);
  const paidOrders = orders.filter(isPaid);
  const selectedTotal = orders
    .filter((order) => selectedReceiptOrderIds.includes(order.id))
    .reduce((sum, order) => sum + Number(order.amount_jpy || 0), 0);

  const toggleReceiptOrder = (orderId) => {
    setSelectedReceiptOrderIds((prev) => (
      prev.includes(orderId)
        ? prev.filter((id) => id !== orderId)
        : [...prev, orderId]
    ));
  };

  const selectAllPaidOrders = () => {
    setSelectedReceiptOrderIds(paidOrders.map((order) => order.id));
  };

  const clearSelectedReceiptOrders = () => {
    setSelectedReceiptOrderIds([]);
  };

  const handleCombinedReceiptDownload = async () => {
    if (selectedReceiptOrderIds.length === 0) {
      setReceiptMessage(copy.payments.selectAtLeastOne);
      return;
    }
    setReceiptLoading(true);
    setReceiptMessage("");
    try {
      await downloadCombinedReceipt({
        orderIds: selectedReceiptOrderIds,
        receiptName,
        token: localStorage.getItem(retailTokenKey) || "",
      });
      setReceiptMessage(copy.payments.generated);
    } catch (err) {
      setReceiptMessage(err.response?.data?.detail || copy.payments.failed);
    } finally {
      setReceiptLoading(false);
    }
  };

  return (
    <section className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_360px]">
      <div className="rounded-[34px] border border-white/10 bg-[#0d0c0a] p-5">
        <SectionHeader title={copy.payments.title} body={copy.payments.body} />
        <div className="mt-5 rounded-3xl border border-[#d8b769]/20 bg-[#d8b769]/8 p-4">
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
            <div>
              <p className="text-sm font-semibold text-[#f3d98b]">{copy.payments.combinedTitle}</p>
              <p className="mt-1 text-xs leading-5 text-white/52">
                {copy.payments.combinedBody}
              </p>
              <label className="mt-4 grid gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-white/42">
                {copy.payments.receiptName}
                <input
                  className="rounded-2xl border border-white/10 bg-white px-4 py-3 text-sm text-black outline-none"
                  value={receiptName}
                  onChange={(event) => setReceiptName(event.target.value)}
                  placeholder={copy.payments.receiptNamePlaceholder}
                />
              </label>
            </div>
            <div className="rounded-3xl border border-white/10 bg-black/25 p-4 lg:min-w-[230px]">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/40">{copy.payments.selectedTotal}</p>
              <p className="mt-1 text-2xl font-semibold text-[#f3d98b]">{formatJPY(selectedTotal)}</p>
              <p className="mt-1 text-xs text-white/42">{fillTemplate(copy.payments.selectedCount, { count: selectedReceiptOrderIds.length })}</p>
              <button
                type="button"
                onClick={handleCombinedReceiptDownload}
                disabled={receiptLoading || selectedReceiptOrderIds.length === 0}
                className="mt-4 w-full rounded-2xl bg-[#d8b769] px-4 py-3 text-sm font-bold text-black transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-45"
              >
                {receiptLoading ? copy.payments.generating : copy.payments.downloadCombined}
              </button>
              {receiptMessage && <p className="mt-3 text-xs leading-5 text-[#f3d98b]">{receiptMessage}</p>}
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button type="button" onClick={selectAllPaidOrders} className="rounded-full border border-[#d8b769]/30 px-3 py-1 text-xs font-semibold text-[#f3d98b] hover:bg-[#d8b769]/10">
              {copy.payments.selectAllPaid}
            </button>
            <button type="button" onClick={clearSelectedReceiptOrders} className="rounded-full border border-white/10 px-3 py-1 text-xs font-semibold text-white/55 hover:text-white">
              {copy.payments.clear}
            </button>
          </div>
        </div>
        <div className="mt-4 grid gap-3">
          {orders.length === 0 ? (
            <EmptyState text={copy.payments.noRecords} />
          ) : (
            orders.map((order) => (
              <article key={order.id} className="rounded-3xl border border-white/10 bg-black/25 p-4">
                <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
                  <div className="flex min-w-0 items-start gap-3">
                    <label className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border ${isPaid(order) ? "border-[#d8b769]/45 bg-[#d8b769]/10" : "border-white/10 bg-white/[0.03]"}`}>
                      <input
                        className="h-4 w-4 accent-[#d8b769]"
                        type="checkbox"
                        checked={selectedReceiptOrderIds.includes(order.id)}
                        disabled={!isPaid(order)}
                        onChange={() => toggleReceiptOrder(order.id)}
                        aria-label={`${copy.payments.combinedTitle}: ${order.confirmation_code || order.order_no || copy.common.reservation}`}
                      />
                    </label>
                    <div className="min-w-0">
                      <p className="break-words text-sm font-semibold">{order.confirmation_code || order.order_no || copy.common.reservation}</p>
                    <p className="mt-1 text-xs text-white/45">{order.departure_date || order.created_at || copy.common.paymentDatePending} · {copy.common.squarePayment}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                    <span className="font-semibold text-[#f3d98b]">{formatJPY(order.amount_jpy)}</span>
                    <StatusPill label={paymentStatusLabel(order, copy)} tone={isPaid(order) ? "green" : isRefunded(order) ? "muted" : "red"} />
                    {isPaid(order) ? (
                      <button type="button" onClick={() => downloadReceipt(order, localStorage.getItem(retailTokenKey) || "")} className="rounded-full border border-[#d8b769]/30 px-3 py-1 text-xs font-semibold text-[#f3d98b] hover:bg-[#d8b769]/10">
                        {copy.common.downloadReceiptPdf}
                      </button>
                    ) : (
                      <span className="rounded-full border border-white/10 px-3 py-1 text-xs font-semibold text-white/35">{copy.common.afterPayment}</span>
                    )}
                  </div>
                </div>
              </article>
            ))
          )}
        </div>
      </div>
      <ReceiptPreview copy={copy} />
    </section>
  );
}

function ReceiptPreview({ copy }) {
  return (
    <aside className="rounded-[34px] border border-[#d8b769]/20 bg-[#0d0c0a] p-5">
      <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#d8b769]">{copy.receiptGuide.title}</p>
      <h3 className="mt-3 text-2xl font-semibold leading-tight">{copy.receiptGuide.headline}</h3>
      <p className="mt-3 text-sm leading-7 text-white/58">
        {copy.receiptGuide.body}
      </p>

      <div className="mt-5 rounded-3xl border border-[#d8b769]/18 bg-[#d8b769]/8 p-4">
        <p className="text-sm font-semibold text-[#f3d98b]">{copy.receiptGuide.before}</p>
        <div className="mt-3 grid gap-3 text-sm leading-6 text-white/62">
          <GuideRow number="1" title={copy.receiptGuide.step1Title} body={copy.receiptGuide.step1Body} />
          <GuideRow number="2" title={copy.receiptGuide.step2Title} body={copy.receiptGuide.step2Body} />
          <GuideRow number="3" title={copy.receiptGuide.step3Title} body={copy.receiptGuide.step3Body} />
        </div>
      </div>

      <div className="mt-4 rounded-3xl border border-white/10 bg-black/25 p-4">
        <p className="text-sm font-semibold">{copy.receiptGuide.includesTitle}</p>
        <div className="mt-3 grid gap-2 text-xs leading-5 text-white/52">
          {copy.receiptGuide.includes.map((item) => <p key={item}>• {item}</p>)}
        </div>
      </div>
    </aside>
  );
}

function GuideRow({ number, title, body }) {
  return (
    <div className="flex gap-3">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#d8b769] text-xs font-black text-black">{number}</span>
      <span>
        <span className="block font-semibold text-white">{title}</span>
        <span className="mt-0.5 block text-xs leading-5 text-white/52">{body}</span>
      </span>
    </div>
  );
}

function Rewards({ overview, ledger, copy, onRedeemed }) {
  const couponEntries = ledger.filter((entry) => Number(entry.credit_delta_jpy || 0) > 0);
  const [redeemCode, setRedeemCode] = useState("");
  const [redeemMessage, setRedeemMessage] = useState("");
  const [redeemLoading, setRedeemLoading] = useState(false);

  const redeemCoupon = async () => {
    const code = redeemCode.trim();
    if (!code) {
      setRedeemMessage(copy.rewards.redeemFailed);
      return;
    }
    setRedeemLoading(true);
    setRedeemMessage("");
    try {
      await axios.post(`${API}/public-booking/member/coupons/redeem`, { code }, {
        headers: { "X-Retail-Member-Token": localStorage.getItem(retailTokenKey) || "" },
      });
      setRedeemCode("");
      setRedeemMessage(copy.rewards.redeemSuccess);
      onRedeemed?.();
    } catch (err) {
      setRedeemMessage(err.response?.data?.detail || copy.rewards.redeemFailed);
    } finally {
      setRedeemLoading(false);
    }
  };

  return (
    <section className="rounded-[34px] border border-white/10 bg-[#0d0c0a] p-5">
      <SectionHeader title={copy.rewards.title} body={copy.rewards.body} />
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <MetricCard icon={<WalletCards className="h-5 w-5" />} label={copy.rewards.availableCredit} value={formatJPY(overview.credit)} />
        <MetricCard icon={<Coins className="h-5 w-5" />} label={copy.rewards.pointsBalance} value={overview.points.toLocaleString("ja-JP")} />
        <MetricCard icon={<Ticket className="h-5 w-5" />} label={copy.rewards.savedCoupons} value={couponEntries.length} />
      </div>
      <div className="mt-4 grid gap-2 text-sm leading-6 text-white/60">
        <p>{copy.rewards.pointRule}</p>
        <p>{copy.rewards.reviewRule}</p>
        <p>{copy.rewards.cashRule}</p>
      </div>
      <div className="mt-5 rounded-3xl border border-[#d8b769]/18 bg-[#d8b769]/8 p-4">
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(280px,420px)] lg:items-start">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[#d8b769] text-black">
              <Ticket className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-semibold text-[#f3d98b]">{copy.rewards.couponTitle}</p>
              <p className="mt-1 text-xs leading-5 text-white/50">{copy.rewards.couponBody}</p>
            </div>
          </div>
          <div className="rounded-3xl border border-[#d8b769]/20 bg-black/35 p-4">
            <p className="text-sm font-semibold text-white">{copy.rewards.redeemTitle}</p>
            <p className="mt-1 text-xs leading-5 text-white/48">{copy.rewards.redeemBody}</p>
            <div className="mt-3 grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
              <input
                className="min-w-0 rounded-2xl border border-white/10 bg-white px-4 py-3 text-sm font-semibold uppercase tracking-[0.08em] text-black outline-none placeholder:normal-case placeholder:tracking-normal"
                value={redeemCode}
                onChange={(event) => setRedeemCode(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") redeemCoupon();
                }}
                placeholder={copy.rewards.redeemPlaceholder}
              />
              <button
                type="button"
                onClick={redeemCoupon}
                disabled={redeemLoading || !redeemCode.trim()}
                className="rounded-2xl bg-[#d8b769] px-5 py-3 text-sm font-black text-black transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-45"
              >
                {redeemLoading ? copy.rewards.redeeming : copy.rewards.redeemButton}
              </button>
            </div>
            {redeemMessage && <p className="mt-3 text-xs leading-5 text-[#f3d98b]">{redeemMessage}</p>}
          </div>
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          {couponEntries.length === 0 ? (
            <EmptyState text={copy.rewards.noCoupons} />
          ) : (
            couponEntries.map((entry) => <CouponCard key={entry.id} entry={entry} copy={copy} />)
          )}
        </div>
      </div>
      <p className="mt-5 text-xs font-semibold uppercase tracking-[0.18em] text-white/35">{copy.rewards.rewardHistory}</p>
      <div className="mt-4 grid gap-3">
        {ledger.length === 0 ? <EmptyState text={copy.rewards.noHistory} /> : ledger.map((entry) => <LedgerCard key={entry.id} entry={entry} copy={copy} />)}
      </div>
    </section>
  );
}

function Profile({ form, setForm, onSave, message, saving, token, copy }) {
  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [passwordMessage, setPasswordMessage] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);
  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const updatePassword = (field, value) => setPasswordForm((prev) => ({ ...prev, [field]: value }));

  const changePassword = async () => {
    if (!token) return;
    setPasswordSaving(true);
    setPasswordMessage("");
    try {
      await axios.post(`${API}/public-booking/member/password/change`, passwordForm, {
        headers: { "X-Retail-Member-Token": token },
      });
      setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
      setPasswordMessage(copy.profile.passwordUpdated);
    } catch (err) {
      setPasswordMessage(err.response?.data?.detail || copy.profile.passwordFailed);
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <section data-testid="profile-lightweight-flow" className="profile-flow-surface px-1 py-2 text-white sm:px-2">
      <div className="pb-5">
        <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#d8b769]">{copy.profile.title}</p>
        <p className="mt-2 max-w-2xl text-sm leading-6 text-white/50">{copy.profile.body}</p>
      </div>

      <div className="divide-y divide-[#d8b769]/12">
        <div data-testid="profile-flow-section-account" className="grid gap-4 py-5 lg:grid-cols-[150px_minmax(0,1fr)]">
          <div>
            <p className="flex items-center gap-2 text-sm font-semibold text-[#f6e6ad]">
              <UserRound className="h-4 w-4 text-[#d8b769]" />
              {copy.profile.personalTitle}
            </p>
            <p className="mt-2 text-xs leading-5 text-white/38">{copy.profile.personalBody}</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <ProfileField label={copy.profile.name} value={form.full_name} onChange={(value) => update("full_name", value)} />
            <ProfileField label={copy.profile.emailLoginId} value={form.email} onChange={(value) => update("email", value)} disabled />
            <ProfileField label={copy.profile.phone} value={form.phone} onChange={(value) => update("phone", value)} />
            <label className="grid gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-white/42">
              {copy.profile.countryRegion}
              <select className="profile-flow-input" value={form.country_region} onChange={(event) => update("country_region", event.target.value)}>
                {countryOptions.map((item) => <option key={item.value} value={item.value}>{item.labels[form.preferred_language] || item.labels.en}</option>)}
              </select>
            </label>
            <label className="grid gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-white/42 sm:col-span-2">
              {copy.profile.preferredLanguage}
              <select className="profile-flow-input" value={form.preferred_language} onChange={(event) => update("preferred_language", event.target.value)}>
                {languageOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </label>
          </div>
        </div>

        <div data-testid="profile-flow-section-receipt" className="grid gap-4 py-5 lg:grid-cols-[150px_minmax(0,1fr)]">
          <div>
            <p className="flex items-center gap-2 text-sm font-semibold text-[#f6e6ad]">
              <ShieldCheck className="h-4 w-4 text-[#d8b769]" />
              {copy.profile.receiptTitle}
            </p>
            <p className="mt-2 text-xs leading-5 text-white/38">{copy.profile.receiptBody}</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <ProfileField label={copy.profile.receiptName} value={form.receipt_name} onChange={(value) => update("receipt_name", value)} placeholder={copy.profile.receiptNamePlaceholder} />
            <ProfileField label={copy.profile.companyName} value={form.company_name} onChange={(value) => update("company_name", value)} placeholder={copy.profile.companyNamePlaceholder} />
            <ProfileField label={copy.profile.billingAddress} value={form.billing_address} onChange={(value) => update("billing_address", value)} placeholder={copy.profile.billingAddressPlaceholder} className="sm:col-span-2" />
            <div className="sm:col-span-2">
              <button type="button" onClick={onSave} disabled={saving} className="rounded-full bg-[#d8b769] px-5 py-2.5 text-sm font-bold text-black transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60">
                {saving ? copy.profile.saving : copy.profile.save}
              </button>
              {message && <p className="mt-3 text-xs leading-5 text-[#f3d98b]">{message}</p>}
            </div>
          </div>
        </div>

        <div data-testid="profile-flow-section-password" className="grid gap-4 py-5 lg:grid-cols-[150px_minmax(0,1fr)]">
          <div>
            <p className="flex items-center gap-2 text-sm font-semibold text-[#f6e6ad]">
              <KeyRound className="h-4 w-4 text-[#d8b769]" />
              {copy.profile.changePassword}
            </p>
            <p className="mt-2 text-xs leading-5 text-white/38">{copy.profile.passwordBody}</p>
          </div>
          <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_160px] lg:items-end">
            <ProfileField label={copy.profile.currentPassword} type="password" value={passwordForm.current_password} onChange={(value) => updatePassword("current_password", value)} />
            <ProfileField label={copy.profile.newPassword} type="password" value={passwordForm.new_password} onChange={(value) => updatePassword("new_password", value)} />
            <ProfileField label={copy.profile.confirmNewPassword} type="password" value={passwordForm.confirm_password} onChange={(value) => updatePassword("confirm_password", value)} />
            <div className="flex flex-col items-stretch lg:items-end">
              <button type="button" onClick={changePassword} disabled={passwordSaving} className="inline-flex min-h-[42px] w-full items-center justify-center rounded-full border border-[#d8b769]/35 bg-[#d8b769]/10 px-5 py-2.5 text-sm font-bold text-[#f3d98b] transition hover:bg-[#d8b769] hover:text-black disabled:cursor-not-allowed disabled:opacity-60 lg:w-[160px] lg:whitespace-nowrap">
                {passwordSaving ? copy.profile.updating : copy.profile.updatePassword}
              </button>
            </div>
            {passwordMessage && <p className="text-xs leading-5 text-[#f3d98b] lg:col-span-4">{passwordMessage}</p>}
          </div>
        </div>
      </div>
    </section>
  );
}

function Support({ copy }) {
  const whatsappUrl = `https://wa.me/${whatsappPhone}`;
  return (
    <section className="rounded-[34px] border border-[#d8b769]/20 bg-[#0d0c0a] p-5">
      <SectionHeader title={copy.support.title} body={copy.support.body} />
      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <SupportLink icon={<Mail className="h-5 w-5" />} title={copy.support.email} body={supportEmail} href={`mailto:${supportEmail}`} />
        <SupportLink icon={<MessageCircle className="h-5 w-5" />} title={copy.support.whatsapp} body={copy.support.whatsappBody} href={whatsappUrl} />
        <SupportLink icon={<Bot className="h-5 w-5" />} title={copy.support.ai} body={copy.support.aiBody} href="#ai-concierge" />
      </div>
    </section>
  );
}

function OrderDetailModal({ order, onClose, copy }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 px-4 py-6 backdrop-blur-sm">
      <section className="max-h-[88vh] w-full max-w-2xl overflow-auto rounded-[34px] border border-[#d8b769]/25 bg-[#0d0c0a] p-5 text-white shadow-2xl shadow-black/70">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#d8b769]">{copy.detail.title}</p>
            <h2 className="mt-2 break-words text-2xl font-semibold">{order.confirmation_code || order.order_no || copy.common.reservation}</h2>
          </div>
          <button type="button" onClick={onClose} className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-white/60 hover:text-white" aria-label={copy.common.close}>
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <InfoLine label={copy.common.customerName} value={order.customer_name || copy.common.toBeConfirmed} />
          <InfoLine label={copy.common.customerCode} value={order.customer_code || copy.common.notSet} />
          <InfoLine label={copy.common.serviceDate} value={`${order.departure_date || copy.common.datePending} ${order.pickup_time || ""}`} />
          <InfoLine label={copy.common.serviceType} value={formatServiceType(order, copy)} />
          <InfoLine label={copy.common.pickupLocation} value={order.pickup_location || copy.common.toBeConfirmed} />
          <InfoLine label={copy.common.dropoffLocation} value={order.destination || copy.common.toBeConfirmed} />
          <InfoLine label={copy.common.vehicleType} value={vehicleNames[order.vehicle_type] || order.vehicle_type || copy.common.toBeConfirmed} />
          <InfoLine label={copy.common.reservationStatus} value={reservationStatusLabel(order, copy)} />
          <InfoLine label={copy.common.paymentStatus} value={paymentStatusLabel(order, copy)} />
          <InfoLine label={copy.common.amount} value={formatJPY(order.amount_jpy)} />
        </div>
        <div className="mt-5 flex flex-wrap gap-2">
          {order.payment_url && <ActionLink href={order.payment_url} label={copy.common.payNow} icon={<CreditCard className="h-3.5 w-3.5" />} />}
          {isPaid(order) && <ActionButton onClick={() => downloadReceipt(order, localStorage.getItem(retailTokenKey) || "")} label={copy.common.downloadReceiptPdf} icon={<Download className="h-3.5 w-3.5" />} />}
        </div>
      </section>
    </div>
  );
}

function SectionHeader({ title, body }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#d8b769]">{title}</p>
      {body && <p className="mt-2 text-sm leading-6 text-white/55">{body}</p>}
    </div>
  );
}

function MetricCard({ icon, label, value, onClick }) {
  const Component = onClick ? "button" : "div";
  return (
    <Component type={onClick ? "button" : undefined} onClick={onClick} className={`w-full rounded-3xl border border-white/10 bg-black/30 p-4 text-left ${onClick ? "transition hover:border-[#d8b769]/45 hover:bg-[#d8b769]/10" : ""}`}>
      <div className="text-[#d8b769]">{icon}</div>
      <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/40">{label}</p>
      <p className="mt-2 break-words text-2xl font-semibold text-[#f3d98b]">{value}</p>
    </Component>
  );
}

function StatusPill({ label, tone = "gold" }) {
  const toneClass = {
    gold: "border-[#d8b769]/30 bg-[#d8b769]/10 text-[#f3d98b]",
    green: "border-emerald-300/25 bg-emerald-300/10 text-emerald-100",
    red: "border-red-300/25 bg-red-400/10 text-red-100",
    muted: "border-white/10 bg-white/[0.05] text-white/60",
  }[tone];
  return <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${toneClass}`}>{label}</span>;
}

function InfoLine({ label, value }) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/35">{label}</p>
      <p className="mt-1 break-words text-white">{value}</p>
    </div>
  );
}

function ProfileField({ label, value, onChange, disabled = false, type = "text", placeholder = "", className = "" }) {
  return (
    <label className={`grid gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-white/42 ${className}`}>
      {label}
      <input
        className="profile-flow-input disabled:cursor-not-allowed disabled:border-[#d8b769]/10 disabled:bg-white/[0.05] disabled:text-white/45"
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        placeholder={placeholder}
      />
    </label>
  );
}

function ActionButton({ label, icon = null, onClick }) {
  return (
    <button type="button" onClick={onClick} className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/[0.05] px-3 py-2 text-xs font-semibold text-white/75 transition hover:border-[#d8b769]/35 hover:text-[#f3d98b]">
      {icon}
      {label}
    </button>
  );
}

function ActionLink({ label, icon = null, href }) {
  return (
    <a href={href} className="inline-flex items-center justify-center gap-2 rounded-2xl border border-[#d8b769]/25 bg-[#d8b769]/10 px-3 py-2 text-xs font-semibold text-[#f3d98b] transition hover:bg-[#d8b769] hover:text-black">
      {icon}
      {label}
    </a>
  );
}

function SupportLink({ icon, title, body, href }) {
  return (
    <a href={href} className="rounded-3xl border border-white/10 bg-black/25 p-4 transition hover:border-[#d8b769]/35">
      <div className="text-[#d8b769]">{icon}</div>
      <p className="mt-3 font-semibold">{title}</p>
      <p className="mt-1 break-words text-sm text-white/55">{body}</p>
    </a>
  );
}

function LedgerCard({ entry, copy }) {
  const credit = Number(entry.credit_delta_jpy || 0);
  const points = Number(entry.points_delta || 0);
  return (
    <article className="rounded-3xl border border-white/10 bg-black/25 p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-sm font-semibold capitalize">{String(entry.entry_type || copy.rewards.defaultLedgerType).replaceAll("_", " ")}</p>
          <p className="mt-1 text-xs text-white/45">{entry.created_at || ""}</p>
          {entry.note && <p className="mt-2 text-sm leading-6 text-white/60">{entry.note}</p>}
        </div>
        <div className="flex flex-wrap gap-2 text-sm font-semibold">
          {credit !== 0 && <span className="rounded-full bg-[#d8b769]/10 px-3 py-1 text-[#f3d98b]">{credit > 0 ? "+" : ""}{formatJPY(credit)}</span>}
          {points !== 0 && <span className="rounded-full bg-white/[0.06] px-3 py-1 text-white">{points > 0 ? "+" : ""}{points.toLocaleString("ja-JP")} {copy.rewards.pointsUnit}</span>}
        </div>
      </div>
    </article>
  );
}

function CouponCard({ entry, copy }) {
  const amount = Number(entry.credit_delta_jpy || 0);
  return (
    <article className="overflow-hidden rounded-3xl border border-[#d8b769]/25 bg-black/35">
      <div className="border-b border-[#d8b769]/15 bg-[#d8b769]/10 px-4 py-3">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#f3d98b]">{copy.rewards.accountCoupon}</p>
        <p className="mt-1 text-2xl font-semibold text-[#f3d98b]">{formatJPY(amount)}</p>
      </div>
      <div className="space-y-2 p-4 text-sm text-white/62">
        <p className="font-semibold text-white">{String(entry.entry_type || copy.rewards.defaultLedgerType).replaceAll("_", " ")}</p>
        <p>{entry.note || copy.rewards.defaultCreditNote}</p>
        <p className="text-xs text-white/38">{copy.rewards.issued}: {entry.created_at || copy.common.datePending}</p>
        <StatusPill label={copy.rewards.savedInBalance} tone="green" />
      </div>
    </article>
  );
}

function EmptyState({ text }) {
  return (
    <div className="rounded-3xl border border-dashed border-white/12 bg-white/[0.03] p-6 text-sm leading-6 text-white/50">
      {text}
    </div>
  );
}
